import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Bell, BellOff, Clock, CalendarCheck, Target, FileText, Shield, Send, Users, Megaphone } from 'lucide-react';
import { isAdmin, isHR, getUser } from '@/lib/auth';
import api from '@/lib/api';

const PREF_CONFIG = [
  { key: 'attendance_reminders', label: 'Attendance Reminders', desc: 'Daily clock-in reminders and late alerts', icon: Clock, color: 'text-blue-600 bg-blue-50' },
  { key: 'leave_updates', label: 'Leave Updates', desc: 'Leave request approvals, rejections, and balance updates', icon: CalendarCheck, color: 'text-green-600 bg-green-50' },
  { key: 'kra_reminders', label: 'KRA & Performance', desc: 'Monthly evaluation reminders and quarterly payout alerts', icon: Target, color: 'text-orange-600 bg-orange-50' },
  { key: 'payslip_generated', label: 'Payslip Notifications', desc: 'Alerts when new payslips are generated', icon: FileText, color: 'text-purple-600 bg-purple-50' },
  { key: 'policy_updates', label: 'Policy Updates', desc: 'Company policy changes and new announcements', icon: Shield, color: 'text-red-600 bg-red-50' },
  { key: 'general', label: 'General Notifications', desc: 'System updates and other alerts', icon: Bell, color: 'text-slate-600 bg-slate-50' },
];

export default function NotificationPreferencesPage() {
  const user = getUser();
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subStatus, setSubStatus] = useState(null);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [bcTitle, setBcTitle] = useState('');
  const [bcMessage, setBcMessage] = useState('');
  const [bcCategory, setBcCategory] = useState('general');
  const [bcSending, setBcSending] = useState(false);
  const [scheduledJobs, setScheduledJobs] = useState([]);
  const [triggerLoading, setTriggerLoading] = useState('');

  const fetchPrefs = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        api.get('/api/push/preferences'),
        api.get('/api/push/subscription-status'),
      ]);
      setPrefs(p.data.preferences);
      setSubStatus(s.data);
      // Admin: fetch scheduled jobs
      if (isAdmin()) {
        try {
          const j = await api.get('/api/push/scheduled-jobs');
          setScheduledJobs(j.data.jobs || []);
        } catch { /* not admin */ }
      }
    } catch { toast.error('Failed to load preferences'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const togglePref = (key) => {
    setPrefs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const savePrefs = async () => {
    setSaving(true);
    try {
      await api.put('/api/push/preferences', { preferences: prefs });
      toast.success('Notification preferences saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const handleBroadcast = async () => {
    if (!bcTitle.trim() || !bcMessage.trim()) { toast.error('Title and message required'); return; }
    setBcSending(true);
    try {
      const { data } = await api.post('/api/push/broadcast', {
        title: bcTitle,
        message: bcMessage,
        category: bcCategory,
        target: 'all',
        send_push: true,
      });
      toast.success(data.message);
      setBroadcastOpen(false);
      setBcTitle('');
      setBcMessage('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send'); }
    setBcSending(false);
  };

  const enabledCount = prefs ? Object.values(prefs).filter(Boolean).length : 0;

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="notification-preferences-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0B1B33]">Notifications</h1>
          <p className="text-sm text-slate-500 mt-1">Manage your push notification preferences</p>
        </div>
        {(isAdmin() || isHR()) && (
          <Button
            onClick={() => setBroadcastOpen(true)}
            className="bg-[#0D2850] hover:bg-[#0B2246] gap-2"
            data-testid="broadcast-btn"
          >
            <Megaphone className="w-4 h-4" /> Broadcast
          </Button>
        )}
      </div>

      {/* Subscription Status */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${subStatus?.subscribed ? 'bg-green-100' : 'bg-slate-100'}`}>
                {subStatus?.subscribed ? <Bell className="w-5 h-5 text-green-600" /> : <BellOff className="w-5 h-5 text-slate-400" />}
              </div>
              <div>
                <p className="text-sm font-semibold text-[#0B1B33]">
                  {subStatus?.subscribed ? 'Push Notifications Active' : 'Push Notifications Inactive'}
                </p>
                <p className="text-xs text-slate-500">
                  {subStatus?.subscribed
                    ? `${subStatus.device_count} device(s) registered`
                    : 'Allow notifications in your browser to receive alerts'}
                </p>
              </div>
            </div>
            <Badge variant="outline" className={subStatus?.subscribed ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-50 text-slate-500'}>
              {subStatus?.subscribed ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Preferences */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Notification Categories</CardTitle>
            <span className="text-xs text-slate-500">{enabledCount}/{PREF_CONFIG.length} enabled</span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-400">Loading...</div>
          ) : (
            <div>
              {PREF_CONFIG.map((cfg, i) => (
                <div key={cfg.key}>
                  {i > 0 && <Separator />}
                  <div className="flex items-center gap-4 px-4 py-4" data-testid={`pref-${cfg.key}`}>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${cfg.color}`}>
                      <cfg.icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#0B1B33]">{cfg.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{cfg.desc}</p>
                    </div>
                    <Switch
                      checked={prefs?.[cfg.key] ?? true}
                      onCheckedChange={() => togglePref(cfg.key)}
                      data-testid={`toggle-${cfg.key}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Button
        onClick={savePrefs}
        disabled={saving || loading}
        className="w-full bg-[#0D2850] hover:bg-[#0B2246] h-11"
        data-testid="save-preferences-btn"
      >
        {saving ? 'Saving...' : 'Save Preferences'}
      </Button>

      {/* Scheduled Jobs (Admin) */}
      {isAdmin() && scheduledJobs.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#0D2850]" /> Scheduled Reminders
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {scheduledJobs.map(job => (
              <div key={job.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`scheduled-${job.id}`}>
                <div>
                  <p className="text-sm font-medium text-[#0B1B33]">{job.name}</p>
                  <p className="text-xs text-slate-500">{job.time} (daily)</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs gap-1"
                  disabled={triggerLoading === job.id}
                  onClick={async () => {
                    setTriggerLoading(job.id);
                    try {
                      const { data } = await api.post('/api/push/trigger-reminder', { job_id: job.id });
                      toast.success(data.message);
                    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                    setTriggerLoading('');
                  }}
                  data-testid={`trigger-${job.id}`}
                >
                  <Send className="w-3 h-3" /> {triggerLoading === job.id ? 'Sending...' : 'Trigger Now'}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Admin Broadcast Dialog */}
      <Dialog open={broadcastOpen} onOpenChange={setBroadcastOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-[#0D2850]" /> Send Broadcast Notification
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Category</Label>
              <Select value={bcCategory} onValueChange={setBcCategory}>
                <SelectTrigger className="mt-1" data-testid="bc-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="attendance_reminders">Attendance</SelectItem>
                  <SelectItem value="leave_updates">Leave</SelectItem>
                  <SelectItem value="kra_reminders">KRA & Performance</SelectItem>
                  <SelectItem value="payslip_generated">Payslip</SelectItem>
                  <SelectItem value="policy_updates">Policy Update</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Title</Label>
              <Input
                value={bcTitle}
                onChange={e => setBcTitle(e.target.value)}
                placeholder="e.g., Monthly payslips are ready"
                className="mt-1"
                data-testid="bc-title-input"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Message</Label>
              <Textarea
                value={bcMessage}
                onChange={e => setBcMessage(e.target.value)}
                placeholder="Notification body text..."
                className="mt-1 resize-none"
                rows={3}
                data-testid="bc-message-input"
              />
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Users className="w-4 h-4" />
              <span>This will be sent to all active employees who have this category enabled</span>
            </div>
            <Button
              onClick={handleBroadcast}
              disabled={bcSending}
              className="w-full bg-[#0D2850] hover:bg-[#0B2246] gap-2"
              data-testid="send-broadcast-btn"
            >
              {bcSending ? 'Sending...' : <><Send className="w-4 h-4" /> Send to All Employees</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
